// client/src/utils/alternance-product.ts
import { DateTime } from "luxon";
import { ProductType } from "@/types/content/product";
import { ProductThemeType } from "@/types/content/product_theme";
import { getCatalogDefaultProduct, getDisplayedProducts } from "@/utils/content-catalog";
import { ApprenticeshipOfferType, MatchCriteria } from "@/types/apprenticeship/enterprise";

export const APPRENTICESHIP_DURATIONS_OPTIONS: { value: string; label: string }[] = [
    { value: "420", label: "12 mois" },
    { value: "630", label: "18 mois" },
];
const APPRENTICESHIP_DURATIONS = new Set(APPRENTICESHIP_DURATIONS_OPTIONS.map(o => parseFloat(o.value)));

export function getApprenticeshipDefaultProduct(
    pt: ProductThemeType,
    selectedDuration: number | null = null
): ProductType | null {
    const displayedProducts = getDisplayedProducts(pt.products).filter(p => p.is_active);

    if (pt.domain !== "alternance") {
        return getCatalogDefaultProduct(pt);
    }

    // Si le default_product est une durée alternance connue, on le retourne
    if (pt.default_product && APPRENTICESHIP_DURATIONS.has(pt.default_product.duration)) {
        if (pt.default_product.is_active && pt.default_product.is_displayed) {
            return pt.default_product;
        }
    }
    // Si selectedDuration est définie, on cherche un produit actif+affiché dont la durée correspond au filtre sélectionné
    if (selectedDuration !== null) {
        const match = displayedProducts.find(p => p.duration === selectedDuration);
        if (match) return match;
    }
    // On cherche un produit actif+affiché dont la durée est une durée alternance connue
    const apprenticeshipMatch = displayedProducts.find(p => APPRENTICESHIP_DURATIONS.has(p.duration));
    if (apprenticeshipMatch) return apprenticeshipMatch;
    // On retourne le default_product ou le premier produit affiché de la liste
    return getCatalogDefaultProduct(pt);
}

// ------------------------------------------------------------
// Matching criteria and CV for apprenticeship offers
// ------------------------------------------------------------
export function criteriaForOffer(offer: { apprentissage: boolean; contrat_pro: boolean; pro_a: boolean; rqth: boolean; age_max: number | null }): MatchCriteria {
    return {
        contract: Boolean(offer.apprentissage || offer.contrat_pro || offer.pro_a),
        rqth: Boolean(offer.rqth),
        age: Boolean(offer.age_max),
        closed: false,
        radius: "50"
    };
}

export function criteriaQuery(criteria: MatchCriteria): Record<string, string> {
    return {
        criteria_contract: criteria.contract ? "1" : "0",
        criteria_rqth: criteria.rqth ? "1" : "0",
        criteria_age: criteria.age ? "1" : "0",
        criteria_closed: criteria.closed ? "1" : "0",
        radius: criteria.radius,
        source: "sf"
    };
}

export function cvActionLabel(offer: ApprenticeshipOfferType, override?: { status: "sent" | "refused"; created_at: string }): string {
    const status = override?.status ?? offer.cv_last_action_status;
    const at = override?.created_at ?? offer.cv_last_action_at;
    if (!status || !at) return "-";
    const when = DateTime.fromISO(at).toLocaleString(DateTime.DATE_SHORT);
    return when;
}

export function contractFilterLabel(offer: { apprentissage: boolean; contrat_pro: boolean }): string {
    const kinds = [offer.apprentissage ? "AP" : "", offer.contrat_pro ? "CP" : ""].filter(Boolean);
    return kinds.length ? `Contrat (${kinds.join(" et ")})` : "Contrat";
}
